import DOMPurify from 'isomorphic-dompurify';
import type { Element as ElementType, Page, Section } from '../types/model';
import type { ElementTypes, QuestionTypes } from '@zotlo-shared/types/options';
import type { QuestionOption } from '@zotlo-shared/types/model';
import { isJSON } from './helpers';

export function purify(value: any) {
  return DOMPurify.sanitize(value, { USE_PROFILES: { html: true }, ADD_ATTR: ['style', 'target'] });
}

export function getNestedObject(main: Record<string, any>, fullPath: string) {
  const pathList = fullPath.split('.');
  let temp = main as any;
  let lastKey = '';
  let parent = { key: '', ref: null  };
  let lastPath = '';

  for (const [index, path] of Object.entries(pathList)) {
    const pattern = /(.*?)\[(.*?)\]$/g;
    if (pattern.test(path)) {
      const [key, index] = path.replace(pattern, '$1|$2').split('|');
      if (Number.isNaN(Number.parseFloat(index))) {
        temp = temp?.[key]?.find((item: any) => item.id === index);
      } else {
        temp = temp?.[key]?.[index];
      }
    } else {
      const val = temp?.[path];

      if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
        temp = temp?.[path];
      } else if (typeof val !== 'object') {
        lastKey = path;
      }
    }

    if (Number.parseFloat(index) === pathList.length - 2) {
      parent.ref = temp;
      parent.key = lastKey || path;
    }

    lastPath = path;
  }

  return [temp, lastKey, parent, lastPath];
}

export function setPageLocalizations(page: Page, locale: Record<string, any>) {
  for (const item of Object.entries(locale)) {
    const fullPath = item[0];
    let value = item[1];
    let [refObj, lastKey, parent, lastPath] = getNestedObject(page, fullPath);

    if (!refObj) continue;

    if (lastKey) {
      /*
        Necessary process for conditionalValue
        conditionalValue items can contain HTML string so when purifying JSON with HTML string,
        JSON.parse method cannot parse, throw error and block the app
      */
      if (lastKey === 'conditionalValue') {
        const obj = JSON.parse(value);

        // Purify only values
        for (const key of Object.keys(obj)) {
          obj[key].value = purify(obj[key].value);
        }

        // Convert purified object to JSON string safely
        value = JSON.stringify(obj);
      } else {
        value = purify(value);
      }

      refObj[lastKey] = value;
    } else if (typeof refObj === 'object' && refObj !== null) {
      if (isJSON(value)) {
        value = JSON.parse(value);
      } else if (typeof value === 'string') {
        value = purify(value);
      }
      parent.ref[lastPath] = value;
    } else {
      refObj = purify(value);
    }
  }

  return page;
}

export function getElementsBySlot(elements: ElementType<ElementTypes>[], slotName?: string) {
  return elements.filter((el) => {
    if (!slotName || slotName === 'default') return !el.slotName || el.slotName === 'default';
    return el.slotName === slotName;
  });
}

export function getElementByType(
  elements: ElementType<ElementTypes>[],
  typeName: ElementTypes | boolean | ((type: string) => boolean)
): null | ElementType<ElementTypes> {
  let found: null | ElementType<ElementTypes> = null;

  for (const el of elements) {
    const condition =
      typeof typeName === 'function'
        ? typeName(el.type)
        : typeof typeName === 'boolean'
        ? typeName
        : el.type === typeName;

    if (condition) {
      found = el;
      break;
    }

    found = getElementByType(el.elements, typeName);
    if (found) break;
  }

  return found;
}

export function getElementByTypeInPage(page: string, type: ElementTypes | boolean | ((type: string) => boolean)) {
  let el = null;
  const flowPage = JSON.parse(page);
  for (const section of flowPage.sections) {
    for (const column of section.columns) {
      el = getElementByType(column.elements, type);
      if (el) break;
    }
    if (el) break;
  }
  return el;
}

export function getElementsByType(
  elements: ElementType<ElementTypes>[],
  typeName: ElementTypes | boolean | ((type: string) => boolean)
) {
  const list = [] as ElementType<ElementTypes>[];

  for (const el of elements) {
    const condition =
      typeof typeName === 'function'
        ? typeName(el.type)
        : typeof typeName === 'boolean'
        ? typeName
        : el.type === typeName;

    if (condition) list.push(el);

    const found = getElementsByType(el.elements, typeName);
    if (found) list.push(...found);
  }

  return list;
}

export function getElementsByTypeInSection(
  section: Section,
  typeName: ElementTypes | boolean | ((type: string) => boolean)
) {
  const list = [];
  for (const column of section.columns) {
    const elements = getElementsByType(column.elements, typeName);
    if (!elements) continue;
    list.push(...elements);
  }
  return list;
}

export function getElementsByTypeInSections(
  sections: Section[],
  typeName: ElementTypes | boolean | ((type: string) => boolean)
) {
  const list = [];
  for (const section of sections) {
    const elements = getElementsByTypeInSection(section, typeName);
    list.push(...elements);
  }
  return list;
}

export function getQuestionElement(elements: ElementType<ElementTypes>[]): null | ElementType<QuestionTypes> {
  return getElementByType(elements, (type: string) =>
    /((image)?selection|(date|time)entry|locationpicker|singlerowtext|file|(numeric|weight|height)input|scaledchoice)/g.test(type)
  ) as ElementType<QuestionTypes>;
}

export function getPaywallElement(elements: ElementType<ElementTypes>[]): null | ElementType<'plan-selection'> {
  return getElementByType(elements, (type: string) => /(plan-selection)/g.test(type)) as ElementType<'plan-selection'>;
}

// This function is used to merge the options from the question element with the options from BE
export function getMergedQuestionOptions(questionEl: ElementType<QuestionTypes>, options: QuestionOption[], lang?: string) {
  return (options || []).map((option:QuestionOption, index:number) => {
    const item = questionEl.attr?.options?.find((item) => item?.id === option?.id) || questionEl.attr?.options?.[index] || {};
    const name = option?.locales?.[lang!] || option.name;

    return {
      ...item,
      ...option,
      name,
    }
  });
}

export function prepareFontUrl(fonts: string[], url?: string) {
  url = url || `https://fonts.googleapis.com/css2?display=swap`
  const unique = [...new Set(fonts.filter(Boolean).map((item) => item.split(',').map((val) => val.trim().replace(/("|')/g, ''))).flat())]
    .filter((font) => !/^sans(-serif)?$/g.test(font))
  const qString = unique?.map((font) => `family=${font.replace(/\s/g, '+')}:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900`).join('&');
  return {
    url,
    query: qString,
    full: url + (qString ? `&${qString}` : '')
  }
}

export function filterElementsByConditionalRefs(elements: ElementType<ElementTypes>[], hiddenConditionalRefs: string[]) {
  return elements.filter(element => !(hiddenConditionalRefs || []).includes(element.conditionalRef || ''));
}
